import React, { useMemo, useState } from 'react';
import { AudioLines, CircleHelp, Film, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { BEGINNER_PRESETS, FILTER_PRESETS, type FilterPreset } from '../data/filterCatalog';

interface FilterLibraryProps {
  mode: 'beginner' | 'advanced';
  onAdd: (preset: FilterPreset) => void;
  onDragStart: (event: React.DragEvent<HTMLDivElement>, preset: FilterPreset) => void;
}

const categories = ['全部', '画面', '时间', '色彩', '效果', '音频', '合成', '输出选项'];

const FilterLibrary: React.FC<FilterLibraryProps> = ({ mode, onAdd, onDragStart }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('全部');
  const presets = mode === 'beginner' ? BEGINNER_PRESETS : FILTER_PRESETS;
  const visiblePresets = useMemo(() => presets.filter((preset) => {
    const matchesText = (preset.label + preset.description + preset.category).toLowerCase().includes(search.toLowerCase().trim());
    return matchesText && (category === '全部' || preset.category === category);
  }), [presets, search, category]);

  return (
    <aside className="filter-library" aria-label="滤镜库">
      <div className="panel-heading">
        <div>
          <h2>{mode === 'beginner' ? '添加处理步骤' : '滤镜与选项'}</h2>
          <p>{mode === 'beginner' ? '选择后会加入处理顺序' : '选择添加，也可拖入画布'}</p>
        </div>
        <span className="panel-heading__mark" aria-hidden="true"><SlidersHorizontal size={17} /></span>
      </div>
      <label className="search-field">
        <Search size={15} aria-hidden="true" />
        <span className="sr-only">搜索滤镜</span>
        <input name="filter-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索滤镜" />
        <kbd>/</kbd>
      </label>
      <div className="library-categories" role="tablist" aria-label="滤镜分类">
        {categories.map((item) => (
          <button key={item} type="button" role="tab" aria-selected={category === item} onClick={() => setCategory(item)}>
            {item}
          </button>
        ))}
      </div>
      <div className="preset-list" aria-live="polite">
        {visiblePresets.map((preset) => (
          <div
            className="preset-row"
            key={preset.id}
            draggable={mode === 'advanced'}
            onDragStart={(event) => onDragStart(event, preset)}
          >
            <span className={'preset-icon preset-icon--' + (preset.streamType || 'option')} aria-hidden="true">
              {preset.streamType === 'audio' ? <AudioLines size={17} /> : <Film size={17} />}
            </span>
            <button className="preset-main" type="button" onClick={() => onAdd(preset)}>
              <span className="preset-main__title">{preset.label}</span>
              <span className="preset-main__meta">{preset.category}{preset.operation === 'option' ? ' · 输出参数' : ''}</span>
            </button>
            <button className="icon-button preset-add" type="button" onClick={() => onAdd(preset)} title={'添加' + preset.label} aria-label={'添加' + preset.label}>
              <Plus size={16} />
            </button>
          </div>
        ))}
        {!visiblePresets.length && (
          <div className="library-empty">
            <CircleHelp size={18} />
            <span>没有匹配的滤镜</span>
          </div>
        )}
      </div>
      <footer className="library-footer">
        <span className="library-footer__dot" />
        <span>{visiblePresets.length} 个可用处理项</span>
        <span className="library-footer__hint">拖放进入画布</span>
      </footer>
    </aside>
  );
};

export default FilterLibrary;
